import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const NAV_H = 68;

type NavItem =
  | { label: string; section: string; path?: undefined }
  | { label: string; path: string; section?: undefined };

const NAV_ITEMS: NavItem[] = [
  { label: "About",    section: "about" },
  { label: "Work",     section: "work" },
  // { label: "Projects", section: "projects" }, // Side Quests hidden for now
  { label: "Travel",   section: "travel" },
  { label: "Writing",  section: "writing" },
  { label: "Contact",  section: "contact" },
];

const SPY_IDS = ["about", "work", "travel", "writing", "contact"];

type NavProps = {
  activeSectionId?: string;
};

const Nav = ({ activeSectionId }: NavProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [menuOpen, setMenuOpen] = useState(false);
  const isEssay = location.pathname === "/essay";

  const scrollToSection = (sectionId: string) => {
    if (sectionId === "top") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    const el = document.getElementById(sectionId);
    if (!el) return;
    const y = window.scrollY + el.getBoundingClientRect().top - NAV_H + 1;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const transitionTo = (path: string) => {
    setMenuOpen(false);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const site = document.querySelector(".site");
    if (!site || reduce) { navigate(path); return; }
    site.classList.remove("fx-in");
    site.classList.add("fx-out");
    setTimeout(() => navigate(path), 300);
  };

  const handleSectionClick = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    setMenuOpen(false);
    if (isHome) {
      scrollToSection(sectionId);
    } else {
      // Navigate home, then scroll to section after mount
      transitionTo("/#" + sectionId);
    }
  };

  const handleBrandClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isHome) { scrollToSection("top"); } else { transitionTo("/"); }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      <header className="nav">
        <a className="nav-brand" href={isHome ? "#top" : "/"} onClick={handleBrandClick}>
          Megholova
          <span className="nav-brand-sub">SWE · Field Notes</span>
        </a>

        <nav className="nav-links" aria-label="Primary navigation">
          {NAV_ITEMS.map((item) => {
            if (item.path) {
              return (
                <a
                  key={item.label}
                  href={item.path}
                  className={"nav-link" + (isEssay ? " is-active" : "")}
                  onClick={(e) => { e.preventDefault(); transitionTo(item.path!); }}
                >
                  {item.label}
                </a>
              );
            }
            const isActive = isHome && activeSectionId === item.section;
            return (
              <a
                key={item.label}
                href={isHome ? "#" + item.section : "/#" + item.section}
                className={"nav-link" + (isActive ? " is-active" : "")}
                onClick={(e) => handleSectionClick(e, item.section!)}
              >
                {item.label}
              </a>
            );
          })}
        </nav>

        <button
          className={"nav-hamburger" + (menuOpen ? " is-open" : "")}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span className="nav-hamburger-line" />
          <span className="nav-hamburger-line" />
          <span className="nav-hamburger-line" />
        </button>
      </header>

      <div className={"nav-mobile-overlay" + (menuOpen ? " is-open" : "")} aria-hidden={!menuOpen}>
        {NAV_ITEMS.map((item) => {
          if (item.path) {
            return (
              <a
                key={item.label}
                href={item.path}
                className={"nav-mobile-link" + (isEssay ? " is-active" : "")}
                onClick={(e) => { e.preventDefault(); transitionTo(item.path!); }}
              >
                {item.label}
              </a>
            );
          }
          const isActive = isHome && activeSectionId === item.section;
          return (
            <a
              key={item.label}
              href={isHome ? "#" + item.section : "/#" + item.section}
              className={"nav-mobile-link" + (isActive ? " is-active" : "")}
              onClick={(e) => handleSectionClick(e, item.section!)}
            >
              {item.label}
            </a>
          );
        })}
      </div>
    </>
  );
};

export { NAV_H, SPY_IDS };
export default Nav;
